import type { NavigateFunction } from 'react-router';
import { ROUTES } from '@/shared/config/routes';
import { backToState } from '@/shared/lib/back-to';
import type { RootNavigationState } from '@/types/routing.types';
import { getAgentMetadataHelpers } from './agent-metadata-bindings';

export interface LaunchAgentWithWidgetOptions {
  /** Inputs to pass to the agent. */
  inputs?: Record<string, unknown>;
  /** Merged over the agent's default widget data. */
  customWidgetData?: Record<string, unknown>;
  /** Path the session's breadcrumb should lead back to. */
  backTo?: string;
}

/**
 * Launch an agent with its auto-open widget in a new chat session.
 * This ensures consistent behavior across all entry points (dashboard, springboard, most used tools, etc.)
 *
 * @param navigate - React Router navigate function
 * @param agentId - ID of the agent to launch
 */
export function launchAgentWithWidget(
  navigate: NavigateFunction,
  agentId: string,
  { inputs, customWidgetData, backTo }: LaunchAgentWithWidgetOptions = {},
): void {
  const origin = backTo ? backToState(backTo) : {};
  const { autoOpen, layoutMode, data } = getAgentMetadataHelpers().getInitialWidgetState(agentId);

  if (autoOpen && data) {
    // Agent has auto-open widget - navigate with widgetData like dashboard does
    const baseData = typeof data.data === 'object' && data.data !== null ? data.data : {};
    void navigate(ROUTES.NEW_CHAT_SESSION, {
      state: {
        agentId,
        inputs,
        ...origin,
        widgetData: {
          key: data.key,
          data: customWidgetData ? { ...baseData, ...customWidgetData } : data.data,
          fullWidth: layoutMode === 'fullscreen',
        },
      } satisfies RootNavigationState,
    });
  } else {
    // Agent doesn't have auto-open widget - navigate with just agentId
    void navigate(ROUTES.NEW_CHAT_SESSION, {
      state: {
        agentId,
        inputs,
        ...origin,
      } satisfies RootNavigationState,
    });
  }
}
